import { UseFormReturn, Controller } from "react-hook-form";
import Select from "@/components/Select/Select";
import TextField from "@/components/TextField/TextField";
import { useQuery } from "@apollo/client";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import { useState, useCallback } from "react";
import { NumberFormatBase } from "react-number-format";
import { currencyFormatter, currencyFormatterNoDecimals } from "@/lib/currencyHelpers";
import { JobPostCloseFormInput } from "../types";

interface CloseHireDetailsStepProps {
  form: UseFormReturn<JobPostCloseFormInput>;
}

interface EmployeeData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumbers: { phoneNumber: string }[];
  profilePhoto: string;
}

const COMPENSATION_SCALES = [
  { label: "Not Sure", value: "none" },
  { label: "Salary", value: "Salary" },
  { label: "Weekly", value: "Weekly" },
  { label: "Hourly", value: "Hourly" }
];

const hireIsProOptions: { value: string; label: string }[] = [
  { value: "Not Set", label: "Not Set" },
  { value: "Not Sure", label: "Not Sure" },
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" }
];

const JOB_STATUS_OPTIONS = [
  { value: "Filled", label: "Filled" },
  { value: "Incomplete", label: "Incomplete" }
];

// Helper function to safely convert error messages to string
const getErrorMessage = (error: any): string | undefined => {
  if (!error) return undefined;
  if (typeof error === "string") return error;
  if (error.message && typeof error.message === "string") return error.message;
  return String(error);
};

export function CloseHireDetailsStep({ form }: CloseHireDetailsStepProps) {
  const {
    control,
    formState: { errors },
    clearErrors,
    setValue,
    watch
  } = form;

  // Employee search state and handlers
  const storedEmployeeData = watch("employeeData");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(storedEmployeeData);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);

  const getEmployeeName = useCallback((employee: EmployeeData | null) => {
    if (!employee) return "";
    return `${employee.firstName || ""} ${employee.lastName || ""}`.trim();
  }, []);

  const { data: employeeData, loading: employeeLoading } = useQuery(SOME_PEOPLE_QUERY, {
    variables: {
      input: { search: employeeSearchTerm || "" },
      first: 10
    },
    skip: employeeSearchTerm.length < 2 && !isUserTyping
  });

  const resetEmployeeData = () => {
    setValue("hireeId", "");
    setValue("employeeData", null);
    setSelectedEmployee(null);
    setEmployeeSearchTerm("");
    setIsUserTyping(false);
    clearErrors("hireeId");
    setValue("hireCompensationSchedule", "none");
  };

  const handleEmployeeSelect = (value: EmployeeData) => {
    setValue("hireeId", value.id);
    setValue("employeeData", value);
    setSelectedEmployee(value);
    setEmployeeSearchTerm(getEmployeeName(value));
    setIsUserTyping(false);
    if (errors.hireeId) {
      clearErrors("hireeId");
    }
  };

  const handleEmployeeSearchChange = (string: string) => {
    setIsUserTyping(true);
    setEmployeeSearchTerm(string);

    // If the input is being cleared, reset the employee
    if (!string || string.trim() === "") {
      resetEmployeeData();
      return;
    }

    if (selectedEmployee) {
      const currentName = getEmployeeName(selectedEmployee);

      if (
        string &&
        currentName &&
        !currentName.toLowerCase().includes(string.toLowerCase()) &&
        !string.toLowerCase().includes(currentName.toLowerCase())
      ) {
        setSelectedEmployee(null);
        setValue("hireeId", "", { shouldDirty: true });
      }
    }
  };

  const getEmployeeQuery = () => {
    if (isUserTyping) return employeeSearchTerm;
    if (selectedEmployee) return getEmployeeName(selectedEmployee);
    return employeeSearchTerm;
  };

  return (
    <div className="space-y-6">
      {/* Job Status Section */}
      <Controller
        name="hiringStatus"
        control={control}
        defaultValue="Filled"
        rules={{ required: "Please select a job status" }}
        render={({ field: { onChange, value } }) => (
          <Select
            label="Job Status"
            required
            error={!!errors.hiringStatus}
            errorText={getErrorMessage(errors.hiringStatus?.message)}
            options={JOB_STATUS_OPTIONS}
            onSelect={(value: string) => {
              onChange(value);
            }}
            value={value}
          />
        )}
      />

      {/* Employee Details Section - Only show when status is Filled */}
      <Controller
        name="hireeId"
        control={control}
        render={({ field: { onChange } }) => (
          <Autocomplete
            label="New Employee Hired"
            query={getEmployeeQuery()}
            loadingResults={employeeLoading}
            data={employeeData?.people?.nodes || []}
            onChangeText={handleEmployeeSearchChange}
            reset={resetEmployeeData}
            onSelect={(value: EmployeeData) => {
              onChange(value.id);
              handleEmployeeSelect(value);
            }}
            renderItem={(data: EmployeeData) => (
              <AvatarDetails
                picture={data.profilePhoto}
                title={getEmployeeName(data)}
                text={data.email}
                icon="person"
              />
            )}
            error={!!errors.hireeId}
            errorText={getErrorMessage(errors.hireeId?.message)}
          />
        )}
      />

      <Controller
        name="hireIsPgaPro"
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Hire is PGA Pro"
            options={hireIsProOptions}
            value={value}
            onSelect={onChange}
            error={!!errors.hireIsPgaPro}
            errorText={getErrorMessage(errors.hireIsPgaPro?.message)}
          />
        )}
      />

      {/* Compensation Section - Only show when status is Filled */}
      <div className="flex flex-col space-y-6">
        <Controller
          name="hireCompensationSchedule"
          control={control}
          defaultValue={watch("hireCompensationSchedule") || "none"}
          render={({ field: { value, onChange } }) => (
            <Select
              label="Pay Schedule"
              options={COMPENSATION_SCALES}
              value={value}
              onSelect={onChange}
              error={!!errors.hireCompensationSchedule}
              errorText={getErrorMessage(errors.hireCompensationSchedule?.message)}
            />
          )}
        />
        <div className="flex-1">
          <Controller
            name="previousCompensation"
            control={control}
            render={({ field: { onChange, value } }) => (
              <NumberFormatBase
                disabled={false}
                value={value === null ? "" : value}
                format={(value) => currencyFormatterNoDecimals(value)}
                className="w-full"
                customInput={TextField}
                error={!!errors.previousCompensation}
                errorText={getErrorMessage(errors.previousCompensation?.message)}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => onChange(values.value || null)}
                leadingIcon="attach_money"
                label="Previous Compensation"
              />
            )}
          />
        </div>

        <div className="flex-1">
          <Controller
            name="hireCompensationAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <NumberFormatBase
                disabled={false}
                value={value === null ? "" : value}
                format={currencyFormatter}
                className="w-full"
                customInput={TextField}
                error={!!errors.hireCompensationAmount}
                errorText={getErrorMessage(errors.hireCompensationAmount?.message)}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  onChange(values.formattedValue?.replace(",", "") || null);
                }}
                leadingIcon="attach_money"
                label="New Hire Compensation"
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
